import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthService from '../services/AuthService';
import ApiService from '../services/ApiService';
import logo from '../assets/images/logo.png';
import moment from 'moment';

const PartnerHeader = () => {
    const navigate = useNavigate();
    const [partner, setPartner] = useState(AuthService.getCurrentpartner());
    const [isOnline, setIsOnline] = useState(partner?.is_online === 1 || partner?.is_online === true);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);

    const getPartnerDetails = useCallback(async () => {
        try {
            const res = await ApiService.request({
                method: 'GET',
                url: 'partner/getProfile',
            });
            if (res.data.status) {
                const partnerData = res.data.data?.partner;
                const status = partnerData?.is_online === 1 || partnerData?.is_online === true;
                setIsOnline(status);
                const updatedPartner = { ...partner, ...partnerData, is_online: status };
                setPartner(updatedPartner);
                localStorage.setItem('partner', JSON.stringify(updatedPartner));
            }
        } catch { /* silent */ }
    }, [partner]);

    const handleToggleOnline = async () => {
        const nextStatus = isOnline ? 0 : 1;
        try {
            const res = await ApiService.request({
                method: 'POST',
                url: 'partner/updateProfile',
                data: { is_online: nextStatus }
            });
            if (res.data.status) {
                setIsOnline(!!nextStatus);
                const updatedPartner = { ...partner, is_online: !!nextStatus };
                setPartner(updatedPartner);
                localStorage.setItem('partner', JSON.stringify(updatedPartner));
                // toast.success(res.data.message || `Status updated to ${nextStatus ? 'Online' : 'Offline'}`);
            }
        } catch {
            toast.error('Failed to update online status');
        }
    };

    const getNotifications = useCallback(async () => {
        try {
            const res = await ApiService.request({
                method: 'GET',
                url: 'partner/getNotifications',
                params: { page: 1, limit: 15 },
            });
            if (res.data.status) {
                setNotifications(res.data.data?.notifications || []);
                setUnreadCount(res.data.data?.unread_count ?? 0);
            }
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        // getNotifications();
        // getPartnerDetails();
        const interval = setInterval(() => { getNotifications(); getPartnerDetails(); }, 3000);
        return () => clearInterval(interval);
    }, [getNotifications, getPartnerDetails]);

    const handleMarkAllRead = async () => {
        try {
            await ApiService.request({ method: 'POST', url: 'partner/markReadNotifications', data: { notification_id: 'all' } });
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch { /* silent */ }
    };

    const handleLogout = () => {
        AuthService.partnerLogout();
        setShowLogoutModal(false);
    };

    const logoUrl = partner?.logo
        ? (partner.logo.startsWith('http') ? partner.logo : `${process.env.REACT_APP_API_URL?.replace('/api', '')}/public/uploads/${partner.logo}`)
        : null;

    return (
        <>
        <div id="kt_app_header" className="app-header bg-white">
            <div className="app-container container-fluid d-flex align-items-stretch justify-content-between" id="kt_app_header_container">

                {/* Mobile sidebar toggle */}
                <div className="d-flex align-items-center d-lg-none ms-n2 me-2" title="Show sidebar menu">
                    <div className="btn btn-icon btn-active-color-primary w-35px h-35px" id="kt_app_sidebar_mobile_toggle">
                        <i className="bi bi-list text-light fs-3"></i>
                    </div>
                </div>

                {/* Mobile logo */}
                <div className="d-flex align-items-center flex-grow-1 flex-lg-grow-0">
                    <Link to="/partner/dashboard" className="d-lg-none rounded">
                        <img alt="Logo" src={logo} className="h-50px" style={{ maxHeight: 30 }} />
                    </Link>
                </div>

                {/* Title */}
                <div className="search header-search">
                    <h2>Welcome, {partner?.company_name || 'Partner'}</h2>
                    <p>Manage your services, bookings and account</p>
                </div>

                <div className="d-flex align-items-stretch justify-content-between flex-lg-grow-1" id="kt_app_header_wrapper">
                    <div className="app-header-menu app-header-mobile-drawer align-items-stretch" />

                    <div className="app-navbar flex-shrink-0">

                        {/* Online/Offline toggle */}
                        <div className="app-navbar-item ms-1 ms-lg-3">
                            <div className="form-check form-switch form-check-custom form-check-solid">
                                <input
                                    className="form-check-input h-20px w-35px cursor-pointer"
                                    type="checkbox"
                                    checked={isOnline}
                                    onChange={handleToggleOnline}
                                    id="onlineStatusToggle"
                                />
                                <label
                                    className={`form-check-label fw-bold fs-7 ms-2 ${isOnline ? 'text-base' : 'text-gray-400'}`}
                                    htmlFor="onlineStatusToggle"
                                    style={{ minWidth: 50 }}
                                >
                                    {isOnline ? 'Online' : 'Offline'}
                                </label>
                            </div>
                        </div>

                        {/* Notification bell */}
                        <div
                            className="app-navbar-item ms-1 ms-lg-3 position-relative ph-notif-area"
                            onMouseEnter={() => setShowDropdown(true)}
                            onMouseLeave={() => setShowDropdown(false)}
                        >
                            <div
                                className="btn btn-icon btn-custom btn-active-dark btn-active-color-dark w-35px h-35px w-md-40px h-md-40px position-relative"
                                onClick={() => navigate('/partner/notifications')}
                                style={{ cursor: 'pointer' }}
                            >
                                <i className="bi bi-bell-fill text-gray-500 fs-2"></i>
                                {unreadCount > 0 && (
                                    <span className="bullet bullet-dot h-20px w-20px position-absolute translate-middle top-5 start-75 bg-danger text-white">
                                        <small>{unreadCount}</small>
                                    </span>
                                )}
                            </div>

                            {/* Notification dropdown */}
                            <div style={{
                                position: 'absolute', right: 0, top: 'calc(100% + 10px)', width: 340,
                                background: '#fff', borderRadius: 8, zIndex: 9999,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb',
                                overflow: 'hidden',
                                opacity: showDropdown ? 1 : 0,
                                visibility: showDropdown ? 'visible' : 'hidden',
                                transform: showDropdown ? 'translateY(0)' : 'translateY(-10px)',
                                transition: 'all 0.25s ease',
                            }}>
                                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>Notifications</span>
                                    {unreadCount > 0 && (
                                        <button onClick={handleMarkAllRead}  className='text-base'
                                            style={{ background: 'none', border: 'none', fontSize: 12,  cursor: 'pointer', fontWeight: 600 }}>
                                            Mark all read
                                        </button>
                                    )}
                                </div>
                                <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                                    {notifications.length === 0 ? (
                                        <div style={{ padding: '24px 16px', textAlign: 'center', color: '#aaa', fontSize: 14 }}>
                                            <i className="bi bi-bell-slash fs-2x text-gray-400 d-block mb-3"></i>
                                            No notifications
                                        </div>
                                    ) : notifications.map((n, i) => (
                                        <div key={i} style={{
                                            padding: '12px 16px', borderBottom: '1px solid #f9f9f9',
                                            background: n.is_read ? '#fff' : '#fdfbf5ff',
                                        }}>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{n.title}</div>
                                            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{n.message}</div>
                                            <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{moment(n.createdAt).fromNow()}</div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ padding: '12px 16px', borderTop: '1px solid #f5f5f5', textAlign: 'center' }}>
                                    <Link
                                        to="/partner/notifications"
                                        className="text-base"
                                        onClick={() => setShowDropdown(false)}
                                        style={{ fontSize: 13, fontWeight: 600,}}
                                    >
                                        View All Notifications
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Profile dropdown */}
                        <div className="app-navbar-item ms-1 ms-lg-3 position-relative" id="kt_header_user_menu_toggle">
                            <div className="cursor-pointer symbol symbol-35px symbol-md-40px profile-dropdown position-relative">
                                {logoUrl
                                    ? <img src={logoUrl} alt="partner" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover' }} />
                                    : <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#c4941a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15 }}>
                                        {partner?.company_name?.[0]?.toUpperCase() || 'P'}
                                      </div>
                                }
                                <div className="header-profile-popup">
                                    <div className="header-profile-content">
                                        <div className="header-profile-info">
                                            <div className="header-profile-avatar">
                                                {logoUrl
                                                    ? <img alt="partner" src={logoUrl} />
                                                    : <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#c4941a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20 }}>
                                                        {partner?.company_name?.[0]?.toUpperCase() || 'P'}
                                                      </div>
                                                }
                                            </div>
                                            <div className="header-profile-details">
                                                <div className="header-profile-name">{partner?.company_name || 'Partner'}</div>
                                                <div className="header-profile-email">{partner?.email || ''}</div>
                                            </div>
                                        </div>
                                        <div className="header-profile-separator"></div>
                                        <div className="header-profile-menu">
                                            <div className="header-profile-menu-item">
                                                <Link to="/partner/profile">Account Settings</Link>
                                            </div>
                                            <div className="header-profile-menu-item">
                                                <button onClick={() => setShowLogoutModal(true)} className="logout-btn-header">Logout</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        {/* Logout confirmation modal */}
        {showLogoutModal && ReactDOM.createPortal(
            <>
                <div
                    onClick={() => setShowLogoutModal(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99998, backdropFilter: 'blur(2px)' }}
                />
                <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', width: '100%', maxWidth: 400, padding: '2rem', pointerEvents: 'auto', textAlign: 'center' }}>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fffef0', margin: '0 auto 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="bi bi-box-arrow-right text-base" style={{ fontSize: 28 }}></i>
                        </div>
                        <h5 style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: '0.5rem' }}>Confirm Logout</h5>
                        <p style={{ color: '#6c757d', fontSize: 14, marginBottom: '1.75rem' }}>Are you sure you want to log out of your partner session?</p>
                        <div className="d-flex gap-3 justify-content-center">
                            <button className="btn btn-light fw-semibold px-4" style={{ borderRadius: 10, minWidth: 110 }} onClick={() => setShowLogoutModal(false)}>Cancel</button>
                            <button className="btn fw-semibold px-4 btn-base" style={{ borderRadius: 10, minWidth: 110 }} onClick={handleLogout}>
                                <i className="bi bi-box-arrow-right me-2"></i>Logout
                            </button>
                        </div>
                    </div>
                </div>
            </>,
            document.body
        )}
        </>
    );
};

export default PartnerHeader;
